/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/metering.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Closed, and closed for a reason: cost attribution must price every meter, and an unpriced one
 * costs zero — which reads on an invoice as "this tenant used nothing" rather than "we forgot to
 * charge for this". Adding a meter therefore breaks a complete rate card until somebody decides
 * what it costs. `rows_scanned` counts rows the *source* database touched and reported back, never
 * rows Lumiq received — pushdown means there are none (ADR-004). `datasets_catalogued` increments
 * on first catalogue only, never on re-harvest, so a nightly sweep of a stable estate costs
 * nothing: metering must not make the product worse to use.
 */
export type Meter = 'rule_evaluation' | 'rows_scanned' | 'datasets_catalogued' | 'llm_tokens' | 'execution_seconds' | 'api_call';

export const METERS: readonly Meter[] = ['rule_evaluation', 'rows_scanned', 'datasets_catalogued', 'llm_tokens', 'execution_seconds', 'api_call'];

export function isMeter(value: unknown): value is Meter {
  return (['rule_evaluation', 'rows_scanned', 'datasets_catalogued', 'llm_tokens', 'execution_seconds', 'api_call'] as readonly unknown[]).includes(value);
}

export interface UsageLine {
  readonly meter: Meter;
  readonly units: number;
  readonly first_seen: string;
  /** The end of the newest window counted, not the instant of an event. */
  readonly last_seen: string;
}

export function isUsageLine(value: unknown): value is UsageLine {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['rule_evaluation', 'rows_scanned', 'datasets_catalogued', 'llm_tokens', 'execution_seconds', 'api_call'] as readonly unknown[]).includes(candidate['meter']) &&
    typeof candidate['units'] === 'number' &&
    typeof candidate['first_seen'] === 'string' &&
    typeof candidate['last_seen'] === 'string'
  );
}

export interface CostLine {
  readonly meter: Meter;
  readonly units: number;
  /**
   * Decimal as a string. The rate travels with the cost so the line is reproducible; a bill nobody
   * can reproduce is a bill somebody disputes.
   */
  readonly unit_rate: string;
  readonly cost: string;
}

export function isCostLine(value: unknown): value is CostLine {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['rule_evaluation', 'rows_scanned', 'datasets_catalogued', 'llm_tokens', 'execution_seconds', 'api_call'] as readonly unknown[]).includes(candidate['meter']) &&
    typeof candidate['units'] === 'number' &&
    typeof candidate['unit_rate'] === 'string' &&
    typeof candidate['cost'] === 'string'
  );
}

export interface CostSummary {
  /**
   * Null when the tenant has no rate configured at all — reported as "no rate card" rather than
   * defaulting, because an amount labelled with a currency nobody chose is worse than one that
   * says it could not be priced.
   */
  readonly currency: string | null;
  readonly window_start: string;
  readonly window_end: string;
  readonly lines: readonly CostLine[];
  readonly total: string;
  /**
   * Meters with usage in this window and no rate. Always present; empty means everything used was
   * priced.
   */
  readonly unpriced_meters: readonly Meter[];
}

export function isCostSummary(value: unknown): value is CostSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (typeof candidate['currency'] === 'string' || candidate['currency'] === null) &&
    typeof candidate['window_start'] === 'string' &&
    typeof candidate['window_end'] === 'string' &&
    Array.isArray(candidate['lines']) &&
    (candidate['lines'] as readonly unknown[]).every((item) => isCostLine(item)) &&
    typeof candidate['total'] === 'string' &&
    Array.isArray(candidate['unpriced_meters']) &&
    (candidate['unpriced_meters'] as readonly unknown[]).every((item) => isMeter(item))
  );
}

export interface Quota {
  readonly meter: Meter;
  /**
   * A limit of zero is a disabled tenant, not a quota; withhold the entitlement instead of
   * expressing it as a limit nothing can satisfy.
   */
  readonly unit_limit: number;
  /**
   * What happens to the *next* request at the hard limit. Both options are about admission by
   * construction — there is no CANCEL and no ABORT, because degradation must never discard work
   * already accepted. Per quota rather than per platform, because the two shapes of work have
   * opposite needs: a nightly harvest should queue behind the limit, an interactive API call
   * should not wait ten hours to learn it was over.
   */
  readonly on_exhaustion: 'queue' | 'reject';
  /**
   * Where alerting starts, as a fraction of the limit. A quota that only speaks at 100% is a quota
   * that surprises the customer; the soft threshold alerts while still admitting, so the warning
   * costs the tenant nothing.
   */
  readonly soft_ratio: number;
  /**
   * The alerting threshold in units, floored — so the alert fires at or before the ratio rather
   * than one unit late.
   */
  readonly soft_limit: number;
  /**
   * Told to a refused client. Must exceed the counter flush interval, or the herd re-arrives
   * against a counter that has not moved.
   */
  readonly retry_after_seconds: number;
}

export function isQuota(value: unknown): value is Quota {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['rule_evaluation', 'rows_scanned', 'datasets_catalogued', 'llm_tokens', 'execution_seconds', 'api_call'] as readonly unknown[]).includes(candidate['meter']) &&
    typeof candidate['unit_limit'] === 'number' &&
    (['queue', 'reject'] as readonly unknown[]).includes(candidate['on_exhaustion']) &&
    typeof candidate['soft_ratio'] === 'number' &&
    typeof candidate['soft_limit'] === 'number' &&
    typeof candidate['retry_after_seconds'] === 'number'
  );
}
